import { ClientStore, StoreViewState } from 'src/app/stores/stores';
import { APPMODE } from 'src/environments/appmode.enum';
import { environment } from 'src/environments/environment';
import StoreUtils from './StoreUtils';

/**
 * Bottom menu bar pages (story #10267).
 *
 * The app has one URL to view mechanism: a hash fragment reaches
 * `LocationService.loadView` through the router's `Scroll` event, which
 * dispatches the matching view state. The bar and the deep-link routes both go
 * through that single mechanism, so the tables below are the only place a page
 * is tied to its fragment and its view state.
 */

/**
 * Configured tab route (without the leading slash) to `loadView` fragment. The
 * app build keeps a copy of these keys (`BOTTOM_BAR_START_ROUTES` in
 * capacitor-build-utils.js) to validate the first tab: keep the two in step.
 */
export const WIDGET_ROUTE_VIEWS: { [route: string]: string } = {
  // The store dashboard is the menu page, and it is reached with no fragment.
  menu: '',
  stores: 'widget-stores',
  'loyalty-promotions': 'widget-loyalty-promotions',
  'member-code': 'widget-member-code',
  brochure: 'widget-brochure',
  // Pre-existing customer UI view, reused as-is.
  profile: 'profile',
};

/** Route `data.widgetPage` to the same `loadView` fragment. */
export const WIDGET_PAGE_VIEWS: { [widgetPage: string]: string } = {
  MENU: WIDGET_ROUTE_VIEWS.menu,
  STORES: WIDGET_ROUTE_VIEWS.stores,
  LOYALTY_PROMOTIONS: WIDGET_ROUTE_VIEWS['loyalty-promotions'],
  MEMBER_CODE: WIDGET_ROUTE_VIEWS['member-code'],
  BROCHURE: WIDGET_ROUTE_VIEWS.brochure,
  PROFILE: WIDGET_ROUTE_VIEWS.profile,
};

/** View state rendered by each tab, used to derive the active tab. */
export const WIDGET_ROUTE_STATES: {
  [route: string]: StoreViewState['state'];
} = {
  stores: 'STORES',
  'loyalty-promotions': 'LOYALTY_PROMOTIONS',
  'member-code': 'MEMBER_CODE',
  brochure: 'BROCHURE',
  profile: 'PROFILE',
};

/** Whether this build is a Template app that shows the bottom menu bar. */
export function isTemplateBottomBarApp(): boolean {
  return (
    environment.mode === APPMODE.Template &&
    environment.bottomBar?.enabled === true
  );
}

/**
 * Whether a bar page can be shown for `store`, the NgRx selected store (story
 * #11856). The one readiness rule shared by the bar's tab visibility, the
 * start-page claim and `LocationService.loadView` for the pages that depend on
 * the store (Loyalty, Member Code, Brochure), so a tab that shows always opens
 * and a start page is only claimed when its page can render. Stores is the
 * only page before a store is selected; Brochure also needs content. None of
 * the pages reads the catalog, so none of them waits for it.
 */
export function isWidgetRouteAvailable(
  route: string,
  store: ClientStore | null | undefined,
): boolean {
  if (route === 'stores') {
    return true;
  }
  if (!store?.id) {
    return false;
  }
  return route === 'brochure' ? StoreUtils.hasBrochure(store) : true;
}

/** A configured tab route without its leading slash, as the tables above key it. */
export function widgetRoute(route: string | undefined): string {
  const trimmed = (route || '').trim();
  return trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
}

/**
 * The page a bottom-bar template app opens on (story #11856): the first tab of
 * its bar. `null` when the app opens on the Menu as it always has, because it
 * has no bar, its first tab is the Menu, or its first tab has no page of its
 * own (the build rejects that configuration, so this is only a safety net).
 */
export function startPageRoute(): string | null {
  if (!isTemplateBottomBarApp()) {
    return null;
  }
  const route = widgetRoute(environment.bottomBar.tabs?.[0]?.route);
  return WIDGET_ROUTE_STATES[route] ? route : null;
}

/**
 * The `loadView` fragment that re-opens the widget page rendered by `state`,
 * or `null` when no widget page owns it. Used to carry the page a signed-out
 * customer was on through the login screen as its `?next=` parameter.
 */
export function widgetViewFragmentForState(state: string): string | null {
  for (const route of Object.keys(WIDGET_ROUTE_STATES)) {
    if (WIDGET_ROUTE_STATES[route] === state) {
      const fragment = WIDGET_ROUTE_VIEWS[route];
      return fragment ? fragment : null;
    }
  }
  return null;
}

/**
 * Whether `state` renders one of the bottom menu bar's pages. The store
 * dashboard (`LOADED`) is the Menu page. Each of them serves a signed-out
 * customer in place: Menu and Stores need no session, and Loyalty &
 * Promotions, Member Code and My Account offer their own login section.
 */
export function isWidgetPageState(state: string): boolean {
  return (
    state === 'LOADED' ||
    Object.values(WIDGET_ROUTE_STATES).some((pageState) => pageState === state)
  );
}

/**
 * Views a customer opened deliberately, which background work completing later
 * (cart initialization, the initial-view orchestrator) must not replace with
 * the store dashboard.
 */
export const PROTECTED_VIEW_STATES: string[] = [
  'KIOSKVIDEO',
  'CONSUMPTION_MODE',
  'PROMOTION',
  'STORES',
  'LOYALTY_PROMOTIONS',
  'MEMBER_CODE',
  'BROCHURE',
  'PROFILE',
];
